/**
 * The contention map (Design Review V-9; TRD 10.2; Phase 44), shared by the read model behind
 * `GET /forecast/contention`, the browser card and the CSV export, so the three cannot disagree
 * about what a chord is worth, where it is drawn, or what the table says.
 *
 * The question is the one no other view here answers: **which projects compete for the same
 * people**. A Gantt shows the bars, the heatmap shows the load, the matrix shows the cells — and
 * none of them shows the coupling. A person at 95 per cent across four projects is fine until
 * any one of them slips, and then it is four problems.
 *
 * What a chord is worth
 * ---------------------
 * For a pair of projects, per person and per week, the hours the two cannot both have:
 * `min(hours on A, hours on B)`, summed over the people and the weeks of the span. It is the
 * plan's own definition, it is symmetric, and it is zero exactly when nobody is on both.
 *
 * The geometry
 * ------------
 * A chord diagram sizes every arc as **the sum of its own chords**, which is the only sizing
 * under which the ribbons fit the arcs they land on. Two consequences the card has to own:
 *
 *   - a project that shares nobody has an arc of nothing, so it is **not on the ring at all**.
 *     It is named under the chart and carried in the table with `competitors: 0`, which is the
 *     answer to the gate's "a project with no shared people has no chords";
 *   - an arc is *not* the project's size. A small project everybody touches has a long arc and a
 *     large one nobody shares has none. That is the view's subject, not a defect, and the table
 *     carries both figures.
 *
 * The **bipartite alternate** (V-9's own second suggestion, for small teams) draws the same
 * numbers without the minimum: people down the left, projects down the right, a band per
 * assignment as wide as its hours. It answers "who is stretched" where the ring answers "what is
 * coupled", and for a dozen people it is the more readable of the two.
 *
 * Pure: no DOM, no I/O.
 */
import { col } from './table.js';
import { fillForTier } from './patterns.js';
import { typeSlot } from './palette.js';

/** The two spans the map is drawn over. `week` is the span the assignment matrix reconciles to. */
export const CONTENTION_SPANS = Object.freeze(['window', 'week']);

/** What a chord's width stands for. V-9 offers both; the plan's Delivers asks for hours. */
export const CONTENTION_VALUES = Object.freeze(['hours', 'people']);

/** The two renderings of the same response. */
export const CONTENTION_LAYOUTS = Object.freeze(['chord', 'bipartite']);

/** How the ring may be ordered. */
export const CONTENTION_ORDERS = Object.freeze(['shared', 'hours', 'name', 'type']);

/** The kinds of node the map can draw. The non-project baseline is not one of them. */
export const CONTENTION_KINDS = Object.freeze(['project', 'recurring', 'opportunity']);

/**
 * At or under this many people the bipartite alternate is the card's opening layout. V-9 says
 * "for small teams" and does not say how small; a dozen rows is where a ring of arcs stops being
 * denser than a list of lines, and it is a number the gate can move.
 */
export const BIPARTITE_MAX_PEOPLE = 12;

/** The shared filters this view reads, in the API's names. Span and week are the card's own. */
export const CONTENTION_PARAM_KEYS = Object.freeze([
  'run_id',
  'dimension',
  'entity',
  'from',
  'weeks',
  'pipeline',
]);

/** Confidence tiers, most to least confident (PRD 6.1). */
const TIER_ORDER = Object.freeze(['high', 'medium', 'low']);

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/** One key for a node across both facts tables. A retainer is a project: one node, either way. */
export function nodeKeyOf(entityType, entityId) {
  return `${entityType === 'opportunity' ? 'opportunity' : 'project'}:${entityId}`;
}

/** The key of an unordered pair: the two node keys in sort order, so a pair is named once. */
export function pairKeyOf(a, b) {
  return a <= b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * The less confident of two tiers — what a chord wears, because a pair of hours is only as
 * trustworthy as the shakier end of it. Null only when neither end has a tier.
 */
export function weakerTier(a, b) {
  const ai = TIER_ORDER.indexOf(a);
  const bi = TIER_ORDER.indexOf(b);
  if (ai < 0 && bi < 0) return null;
  if (ai < 0) return b;
  if (bi < 0) return a;
  return TIER_ORDER[Math.max(ai, bi)];
}

// ---------------------------------------------------------------------------------------------
// Model

const matches = (node, needle) =>
  [node.name, node.client, node.number, node.source_id]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(needle));

/** A chord's value under the reader's choice of what width means. */
export function chordValue(chord, value = 'hours') {
  return value === 'people' ? (chord?.people ?? 0) : (chord?.shared_hours ?? 0);
}

const orderComparator = (order) => {
  const byName = (a, b) =>
    String(a.name).localeCompare(String(b.name)) || a.key.localeCompare(b.key);
  if (order === 'name') return byName;
  if (order === 'hours') return (a, b) => b.hours - a.hours || byName(a, b);
  if (order === 'type')
    return (a, b) =>
      Number(a.canonical_type == null) - Number(b.canonical_type == null) ||
      String(a.canonical_type ?? '').localeCompare(String(b.canonical_type ?? '')) ||
      b.value - a.value ||
      byName(a, b);
  return (a, b) => b.value - a.value || b.hours - a.hours || byName(a, b);
};

/**
 * Everything the card draws, from a `GET /forecast/contention` response.
 *
 * The controls hide nodes and thin chords in the browser, never on the server, because the
 * response's own figures — a node's `shared_hours`, its `competitors` — are about the whole run
 * and must not follow what is ticked. What the controls hide is counted and stated instead.
 *
 * @param {object} data  a `GET /forecast/contention` response
 * @param {{ value?: 'hours'|'people', order?: string, kinds?: string[], minShared?: number,
 *           q?: string, palette?: object[] }} [opts]
 */
export function contentionModel(
  data,
  {
    value = 'hours',
    order = 'shared',
    kinds = CONTENTION_KINDS,
    minShared = 0,
    q = '',
    palette = [],
  } = {},
) {
  if (!data?.week_start) return null;
  const wanted = new Set(kinds?.length ? kinds : CONTENTION_KINDS);
  const needle = String(q ?? '')
    .trim()
    .toLowerCase();
  const floor = Number(minShared) > 0 ? Number(minShared) : 0;

  const all = (data.nodes ?? []).map((n) => ({
    ...n,
    slot: typeSlot(palette, n.canonical_type),
    fill: n.fill ?? fillForTier(n.confidence_tier),
  }));
  const shownNodes = all.filter((n) => wanted.has(n.kind) && (!needle || matches(n, needle)));
  const shownKeys = new Set(shownNodes.map((n) => n.key));

  const chords = (data.chords ?? [])
    .filter((c) => shownKeys.has(c.a) && shownKeys.has(c.b) && chordValue(c, 'hours') >= floor)
    .map((c) => ({
      ...c,
      value: chordValue(c, value),
      fill: c.fill ?? fillForTier(c.confidence_tier),
    }))
    .filter((c) => c.value > 0);

  // The arc is the sum of the node's own *drawn* chords, which is the only sizing the ribbons
  // fit. A node's response-wide figures travel alongside so the card can say when they differ.
  const drawn = new Map();
  for (const c of chords) {
    for (const key of [c.a, c.b]) {
      const entry = drawn.get(key) ?? { value: 0, hours: 0, competitors: 0 };
      entry.value = round2(entry.value + c.value);
      entry.hours = round2(entry.hours + c.shared_hours);
      entry.competitors += 1;
      drawn.set(key, entry);
    }
  }

  const nodes = shownNodes
    .map((n) => {
      const d = drawn.get(n.key) ?? { value: 0, hours: 0, competitors: 0 };
      return {
        ...n,
        value: d.value,
        drawn_shared_hours: d.hours,
        drawn_competitors: d.competitors,
        isolated: d.competitors === 0,
      };
    })
    .sort(orderComparator(order));

  const ring = nodes.filter((n) => !n.isolated);
  const isolated = nodes.filter((n) => n.isolated);
  const byKey = new Map(nodes.map((n) => [n.key, n]));

  // The bipartite alternate's two columns. A person with no drawn entity left is dropped with
  // the entity that carried them, and counted.
  const edges = (data.edges ?? []).filter((e) => byKey.has(e.entity));
  const withEdges = new Set(edges.map((e) => e.person));
  const people = (data.people ?? [])
    .filter((p) => withEdges.has(p.key))
    .map((p) => {
      const own = edges.filter((e) => e.person === p.key);
      return {
        ...p,
        drawn_hours: round2(own.reduce((s, e) => s + e.hours, 0)),
        drawn_entities: own.length,
      };
    });

  return {
    span: data.span ?? 'window',
    week_start: data.week_start,
    week_end: data.week_end,
    weeks_count: data.weeks_count ?? 1,
    dimension: data.dimension ?? 'company',
    entity: data.entity ?? null,
    pipeline_mode: data.pipeline_mode ?? 'weighted',
    value,
    order,
    kinds: [...wanted],
    min_shared: floor,
    nodes,
    ring,
    isolated,
    chords,
    people,
    edges,
    totals: data.totals ?? null,
    withheld_count: data.withheld_count ?? 0,
    nodes_hidden: all.length - shownNodes.length,
    chords_hidden: (data.chords ?? []).length - chords.length,
    chords_dropped: Math.max(0, (data.totals?.chords ?? 0) - (data.totals?.chords_shown ?? 0)),
  };
}

/** The figures the card's header and notes state, counted over what is drawn. */
export function contentionSummary(model) {
  const chords = model?.chords ?? [];
  const sharedHours = round2(chords.reduce((s, c) => s + c.shared_hours, 0));
  const thickest = chords.reduce(
    (best, c) => (best === null || c.value > best.value ? c : best),
    null,
  );
  const busiest = (model?.people ?? []).reduce(
    (best, p) => (best === null || p.drawn_entities > best.drawn_entities ? p : best),
    null,
  );
  const perKind = Object.fromEntries(CONTENTION_KINDS.map((k) => [k, 0]));
  for (const n of model?.nodes ?? []) perKind[n.kind] = (perKind[n.kind] ?? 0) + 1;
  // The share of the drawn hours that is contended at all: how coupled this portfolio is.
  const hours = round2((model?.nodes ?? []).reduce((s, n) => s + n.hours, 0));
  return {
    nodes: model?.nodes?.length ?? 0,
    ring: model?.ring?.length ?? 0,
    isolated: model?.isolated?.length ?? 0,
    chords: chords.length,
    per_kind: perKind,
    people: model?.people?.length ?? 0,
    hours,
    shared_hours: sharedHours,
    shared_share: hours > 0 ? round2(sharedHours / hours) : null,
    thickest,
    busiest,
    withheld_count: model?.withheld_count ?? 0,
    withheld_hours: model?.totals?.withheld_hours ?? 0,
    unassigned_hours: model?.totals?.unassigned_hours ?? 0,
    nonproject_hours: model?.totals?.nonproject_hours ?? 0,
    nodes_hidden: model?.nodes_hidden ?? 0,
    chords_hidden: model?.chords_hidden ?? 0,
    chords_dropped: model?.chords_dropped ?? 0,
  };
}

/**
 * Which layout to open on. V-9 offers the bipartite graph as the alternate "for small teams",
 * and a team small enough for it is one whose people fit down a column without crowding.
 */
export function suggestLayout(model) {
  const people = model?.people?.length ?? 0;
  if (people === 0) return 'chord';
  return people <= BIPARTITE_MAX_PEOPLE ? 'bipartite' : 'chord';
}

/** Everything touching one node or chord key: what stays lit when the reader hovers it. */
export function incident(model, key) {
  if (!key) return null;
  const chords = new Set();
  const nodes = new Set();
  for (const c of model?.chords ?? []) {
    if (c.key === key || c.a === key || c.b === key) {
      chords.add(c.key);
      nodes.add(c.a);
      nodes.add(c.b);
    }
  }
  if ((model?.nodes ?? []).some((n) => n.key === key)) nodes.add(key);
  return { key, chords, nodes };
}

// ---------------------------------------------------------------------------------------------
// The ring

/** Where a point at `angle` (radians, zero at twelve o'clock, clockwise) sits on a circle. */
export function pointOn(cx, cy, radius, angle) {
  return { x: cx + radius * Math.sin(angle), y: cy - radius * Math.cos(angle) };
}

/** The outer arc of one node, as an SVG path of the band between two radii. */
export function arcPath({ cx, cy, inner, outer, start, end }) {
  const p0 = pointOn(cx, cy, outer, start);
  const p1 = pointOn(cx, cy, outer, end);
  const p2 = pointOn(cx, cy, inner, end);
  const p3 = pointOn(cx, cy, inner, start);
  const large = end - start > Math.PI ? 1 : 0;
  return (
    `M ${r(p0.x)} ${r(p0.y)} A ${r(outer)} ${r(outer)} 0 ${large} 1 ${r(p1.x)} ${r(p1.y)} ` +
    `L ${r(p2.x)} ${r(p2.y)} A ${r(inner)} ${r(inner)} 0 ${large} 0 ${r(p3.x)} ${r(p3.y)} Z`
  );
}

/**
 * One ribbon: the band between two sub-ranges of two arcs, bent through the centre. The
 * quadratic control point is the circle's own centre, which is what makes two ribbons of the
 * same span bend the same way whatever their chord.
 */
export function ribbonPath({ cx, cy, radius, a, b }) {
  const a0 = pointOn(cx, cy, radius, a.start);
  const a1 = pointOn(cx, cy, radius, a.end);
  const b0 = pointOn(cx, cy, radius, b.start);
  const b1 = pointOn(cx, cy, radius, b.end);
  const largeA = a.end - a.start > Math.PI ? 1 : 0;
  const largeB = b.end - b.start > Math.PI ? 1 : 0;
  return (
    `M ${r(a0.x)} ${r(a0.y)} ` +
    `A ${r(radius)} ${r(radius)} 0 ${largeA} 1 ${r(a1.x)} ${r(a1.y)} ` +
    `Q ${r(cx)} ${r(cy)} ${r(b0.x)} ${r(b0.y)} ` +
    `A ${r(radius)} ${r(radius)} 0 ${largeB} 1 ${r(b1.x)} ${r(b1.y)} ` +
    `Q ${r(cx)} ${r(cy)} ${r(a0.x)} ${r(a0.y)} Z`
  );
}

const r = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Where a node's name goes and which way up it reads: radially outward from its arc, flipped on
 * the left half so nothing is upside down. The same treatment every chord diagram uses, and the
 * reason the ring needs a margin as wide as the longest name.
 */
export function arcLabel(arc, { cx = 0, cy = 0, radius, offset = 8 } = {}) {
  const p = pointOn(cx, cy, radius + offset, arc.mid);
  const deg = (arc.mid * 180) / Math.PI;
  const flip = deg > 180;
  return {
    x: r(p.x),
    y: r(p.y),
    anchor: flip ? 'end' : 'start',
    rotate: r(flip ? deg + 90 : deg - 90),
    flip,
  };
}

/**
 * A name cut to the room there is, with an ellipsis so the reader knows it was cut. The whole
 * name is always on the mark itself, in the tooltip and in the table, so nothing is lost.
 */
export function trimLabel(text, max) {
  const name = String(text ?? '');
  if (!Number.isFinite(max) || max <= 0) return '';
  const room = Math.floor(max);
  if (name.length <= room) return name;
  if (room <= 1) return name.slice(0, 1);
  return `${name.slice(0, room - 1)}…`;
}

/** The gap between two arcs, in radians. Enough to tell them apart, never enough to matter. */
export const RING_PAD = 0.02;

/**
 * The ring: an arc per node as long as the chords it carries, and a ribbon per chord landing on
 * a sub-range of each of its two arcs.
 *
 * Each node's ribbons are laid out in the order of their partner around the ring, which is what
 * keeps the ribbons from crossing more than the data forces them to. Angles run clockwise from
 * twelve o'clock, so the first node is at the top and the reader's eye starts where it lands.
 *
 * @param {{ ring: object[], chords: object[] }} model
 * @param {{ cx?: number, cy?: number, radius?: number, thickness?: number, pad?: number }} [opts]
 */
export function ringLayout(
  model,
  { cx = 0, cy = 0, radius = 160, thickness = 12, pad = RING_PAD } = {},
) {
  const nodes = model?.ring ?? [];
  const chords = model?.chords ?? [];
  const total = nodes.reduce((s, n) => s + n.value, 0);
  if (!nodes.length || total <= 0) return { arcs: [], ribbons: [], total: 0, scale: 0 };

  // The pads eat into the circle before the values are laid out, so the arcs always add up.
  const pads = pad * nodes.length;
  const usable = Math.max(0, Math.PI * 2 - pads);
  const scale = usable / total;
  const index = new Map(nodes.map((n, i) => [n.key, i]));

  const arcs = [];
  let angle = 0;
  const cursors = new Map();
  for (const node of nodes) {
    const span = node.value * scale;
    arcs.push({
      key: node.key,
      node,
      start: angle,
      end: angle + span,
      mid: angle + span / 2,
      span,
      path: arcPath({
        cx,
        cy,
        inner: radius,
        outer: radius + thickness,
        start: angle,
        end: angle + span,
      }),
      point: pointOn(cx, cy, radius + thickness, angle + span / 2),
      label: pointOn(cx, cy, radius + thickness + 8, angle + span / 2),
    });
    cursors.set(node.key, angle);
    angle += span + pad;
  }

  // Each end's sub-ranges, taken in the order of the partner around the ring.
  const ends = new Map(nodes.map((n) => [n.key, []]));
  for (const chord of chords) {
    if (!index.has(chord.a) || !index.has(chord.b)) continue;
    ends.get(chord.a).push({ chord, partner: chord.b });
    ends.get(chord.b).push({ chord, partner: chord.a });
  }
  const ranges = new Map();
  for (const node of nodes) {
    const own = ends
      .get(node.key)
      .sort(
        (x, y) =>
          index.get(x.partner) - index.get(y.partner) || x.chord.key.localeCompare(y.chord.key),
      );
    let cursor = cursors.get(node.key);
    for (const { chord } of own) {
      const span = chord.value * scale;
      ranges.set(`${chord.key}@${node.key}`, { start: cursor, end: cursor + span });
      cursor += span;
    }
  }

  const ribbons = chords
    .filter((c) => index.has(c.a) && index.has(c.b))
    .map((chord) => {
      const a = ranges.get(`${chord.key}@${chord.a}`);
      const b = ranges.get(`${chord.key}@${chord.b}`);
      // The ribbon wears the hue of its thicker end, which is how a chord diagram is read: a
      // project's competitors radiate its colour.
      const source = (chord.a_hours ?? 0) >= (chord.b_hours ?? 0) ? chord.a : chord.b;
      return {
        key: chord.key,
        chord,
        a,
        b,
        source,
        path: ribbonPath({ cx, cy, radius, a, b }),
      };
    });

  return { arcs, ribbons, total, scale, cx, cy, radius, thickness };
}

// ---------------------------------------------------------------------------------------------
// The bipartite alternate

/** A band between two vertical ranges, bent with a pair of cubic handles at the midpoint. */
export function bandPath({ x0, x1, a, b }) {
  const mid = (x0 + x1) / 2;
  return (
    `M ${r(x0)} ${r(a.start)} ` +
    `C ${r(mid)} ${r(a.start)} ${r(mid)} ${r(b.start)} ${r(x1)} ${r(b.start)} ` +
    `L ${r(x1)} ${r(b.end)} ` +
    `C ${r(mid)} ${r(b.end)} ${r(mid)} ${r(a.end)} ${r(x0)} ${r(a.end)} Z`
  );
}

/**
 * The bipartite alternate: people down the left, projects down the right, a band per assignment
 * as tall as its hours on both sides.
 *
 * Unlike the ring this draws the hours themselves and takes no minimum, so it answers a
 * different question with the same data — who is stretched across how much, rather than which
 * pair of projects the stretch couples. A project's column block is its **named people's** hours
 * only: role-only and withheld hours have no band to be drawn in, and the card states them.
 *
 * @param {{ people: object[], nodes: object[], edges: object[] }} model
 * @param {{ width?: number, height?: number, gap?: number, blockWidth?: number }} [opts]
 */
export function bipartiteLayout(
  model,
  { width = 640, height = 420, gap = 4, blockWidth = 10, top = 0 } = {},
) {
  const people = model?.people ?? [];
  const edges = model?.edges ?? [];
  const drawnKeys = new Set();
  for (const e of edges) drawnKeys.add(e.entity);
  const nodes = (model?.nodes ?? []).filter((n) => drawnKeys.has(n.key));
  if (!people.length || !nodes.length) return { left: [], right: [], bands: [], height };

  const leftTotal = people.reduce((s, p) => s + p.drawn_hours, 0);
  const rightTotal = nodes.reduce(
    (s, n) => s + edges.filter((e) => e.entity === n.key).reduce((t, e) => t + e.hours, 0),
    0,
  );
  const leftRoom = Math.max(1, height - gap * Math.max(0, people.length - 1));
  const rightRoom = Math.max(1, height - gap * Math.max(0, nodes.length - 1));
  const leftScale = leftTotal > 0 ? leftRoom / leftTotal : 0;
  const rightScale = rightTotal > 0 ? rightRoom / rightTotal : 0;
  // One scale for both columns, so a band is the same thickness at each end and the eye can
  // follow it: the tighter of the two, so neither column overflows.
  const scale = Math.min(leftScale || Infinity, rightScale || Infinity);
  const unit = Number.isFinite(scale) ? scale : 0;

  const place = (rows, sizeOf, x) => {
    let y = top;
    return rows.map((row) => {
      const h = Math.max(1, sizeOf(row) * unit);
      const box = { key: row.key, row, x, y, height: h, top: y, bottom: y + h, cursor: y };
      y += h + gap;
      return box;
    });
  };

  const left = place(people, (p) => p.drawn_hours, 0);
  const right = place(
    nodes,
    (n) => edges.filter((e) => e.entity === n.key).reduce((t, e) => t + e.hours, 0),
    width - blockWidth,
  );
  const leftBy = new Map(left.map((b) => [b.key, b]));
  const rightBy = new Map(right.map((b) => [b.key, b]));

  // A person's bands in the order of the projects down the right, and a project's in the order
  // of the people down the left: the fewest crossings the data allows.
  const rightIndex = new Map(right.map((b, i) => [b.key, i]));
  const leftIndex = new Map(left.map((b, i) => [b.key, i]));
  const ordered = [...edges].sort(
    (a, b) =>
      (leftIndex.get(a.person) ?? 0) - (leftIndex.get(b.person) ?? 0) ||
      (rightIndex.get(a.entity) ?? 0) - (rightIndex.get(b.entity) ?? 0),
  );
  const bands = [];
  for (const edge of ordered) {
    const l = leftBy.get(edge.person);
    if (!l) continue;
    const span = Math.max(0.5, edge.hours * unit);
    const a = { start: l.cursor, end: l.cursor + span };
    l.cursor += span;
    bands.push({ edge, a, span });
  }
  // The right ends are taken in the left's order too, so the bands of one project stack in the
  // order the people appear rather than in the order the rows arrived.
  for (const band of bands.sort(
    (x, y) =>
      (rightIndex.get(x.edge.entity) ?? 0) - (rightIndex.get(y.edge.entity) ?? 0) ||
      (leftIndex.get(x.edge.person) ?? 0) - (leftIndex.get(y.edge.person) ?? 0),
  )) {
    const rb = rightBy.get(band.edge.entity);
    if (!rb) continue;
    band.b = { start: rb.cursor, end: rb.cursor + band.span };
    rb.cursor += band.span;
    band.path = bandPath({ x0: blockWidth, x1: width - blockWidth, a: band.a, b: band.b });
    band.key = `${band.edge.person}|${band.edge.entity}`;
  }

  return {
    left,
    right,
    bands: bands.filter((b) => b.b),
    unit,
    width,
    height: Math.max(left.at(-1)?.bottom ?? 0, right.at(-1)?.bottom ?? 0),
    blockWidth,
  };
}

// ---------------------------------------------------------------------------------------------
// Exports

/** One row per chord: the pairs, thickest first. The table the gate reads the value off. */
export function contentionChordRows(model) {
  const byKey = new Map((model?.nodes ?? []).map((n) => [n.key, n]));
  return (model?.chords ?? []).map((c) => ({
    a: byKey.get(c.a)?.name ?? c.a_name ?? c.a,
    a_client: byKey.get(c.a)?.client ?? null,
    b: byKey.get(c.b)?.name ?? c.b_name ?? c.b,
    b_client: byKey.get(c.b)?.client ?? null,
    shared_hours: c.shared_hours,
    people: c.people,
    weeks: c.weeks,
    peak_hours: c.peak_hours,
    a_hours: c.a_hours,
    b_hours: c.b_hours,
    confidence_tier: c.confidence_tier ?? null,
    withheld_hours: c.withheld_hours,
    withheld_people: c.withheld_people,
    names: (c.contributors ?? []).map((p) => p.label),
  }));
}

/** One row per node: every project on the map, contended or not. */
export function contentionNodeRows(model) {
  return (model?.nodes ?? []).map((n) => ({
    kind: n.kind,
    name: n.name,
    client: n.client ?? null,
    number: n.number ?? n.source_id ?? null,
    canonical_type: n.canonical_type ?? null,
    status: n.status,
    confidence_tier: n.confidence_tier ?? null,
    hours: n.hours,
    people_hours: n.people_hours,
    unassigned_hours: n.unassigned_hours,
    withheld_hours: n.withheld_hours,
    people: n.people,
    weeks: n.weeks,
    shared_hours: n.shared_hours,
    competitors: n.competitors,
    drawn_shared_hours: n.drawn_shared_hours,
    drawn_competitors: n.drawn_competitors,
    slipped: n.slipped === true,
    burn_risk: n.burn_risk === true,
    stage_name: n.stage_name ?? null,
    probability_used: num(n.probability_used),
  }));
}

/**
 * One row per person and chord: the arithmetic behind every chord, long form. This is the gate's
 * own check — list the people on both projects, add their shared hours by hand — done from the
 * export rather than from a tooltip.
 */
export function contentionContributorRows(model) {
  const byKey = new Map((model?.nodes ?? []).map((n) => [n.key, n]));
  const out = [];
  for (const c of model?.chords ?? [])
    for (const p of c.contributors ?? [])
      out.push({
        a: byKey.get(c.a)?.name ?? c.a_name ?? c.a,
        b: byKey.get(c.b)?.name ?? c.b_name ?? c.b,
        person: p.label,
        department: p.entity?.department ?? null,
        a_hours: p.a_hours,
        b_hours: p.b_hours,
        shared_hours: p.shared_hours,
        peak_hours: p.peak_hours,
        weeks: p.weeks,
      });
  return out;
}

/** One row per person: how far each is spread, which is the bipartite's own left column. */
export function contentionPeopleRows(model) {
  return (model?.people ?? []).map((p) => ({
    person: p.label,
    department: p.entity?.department ?? null,
    office: p.entity?.office ?? null,
    hours: p.hours,
    high_confidence_hours: p.high_confidence_hours,
    entities: p.entities,
    opportunities: p.opportunities,
    shared_hours: p.shared_hours,
    competitors: p.competitors,
  }));
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37): specifications, decorated for the screen and read by the CSV writer.

/** One row per pair: the chords. */
export function contentionChordColumns() {
  return [
    col.text('a', 'Project A'),
    col.text('a_client', 'Client A'),
    col.text('b', 'Project B'),
    col.text('b_client', 'Client B'),
    col.hours('shared_hours', 'Shared'),
    col.count('people', 'People'),
    col.count('weeks', 'Weeks'),
    // The biggest single overlap: one person, one week. Not the pair's worst week, which would
    // be a sum across people; this is the largest minimum any one of them contributes.
    col.hours('peak_hours', 'Worst overlap'),
    col.hours('a_hours', 'A hours'),
    col.hours('b_hours', 'B hours'),
    col.text('confidence_tier', 'Confidence'),
    col.hours('withheld_hours', 'Withheld'),
    col.count('withheld_people', 'Withheld people'),
    col.list('names', 'Shared people'),
  ];
}

/** One row per node: the projects and deals on the map. */
export function contentionNodeColumns() {
  return [
    col.text('kind', 'Kind'),
    col.text('name', 'Name'),
    col.text('client', 'Client'),
    col.text('number', 'Number'),
    col.text('canonical_type', 'Type'),
    col.text('status', 'Status'),
    col.text('confidence_tier', 'Confidence'),
    col.hours('hours', 'Hours'),
    col.hours('people_hours', 'Named people'),
    col.hours('unassigned_hours', 'Role-only'),
    col.hours('withheld_hours', 'Withheld'),
    col.count('people', 'People'),
    col.count('weeks', 'Weeks'),
    col.hours('shared_hours', 'Shared'),
    col.count('competitors', 'Competitors'),
    col.flag('slipped', 'Slipped'),
    col.flag('burn_risk', 'Burn risk'),
    col.text('stage_name', 'Stage'),
    col.percent('probability_used', 'Probability'),
  ];
}

/** One row per person and pair: the minimums a chord is the sum of. */
export function contentionContributorColumns() {
  return [
    col.text('a', 'Project A'),
    col.text('b', 'Project B'),
    col.text('person', 'Person'),
    col.text('department', 'Department'),
    col.hours('a_hours', 'Hours on A'),
    col.hours('b_hours', 'Hours on B'),
    col.hours('shared_hours', 'Shared'),
    col.hours('peak_hours', 'Worst week'),
    col.count('weeks', 'Weeks'),
  ];
}

/** One row per person: the bipartite's left column. */
export function contentionPeopleColumns() {
  return [
    col.text('person', 'Person'),
    col.text('department', 'Department'),
    col.text('office', 'Office'),
    col.hours('hours', 'Hours'),
    col.hours('high_confidence_hours', 'High confidence'),
    col.count('entities', 'Projects'),
    col.count('opportunities', 'Deals'),
    col.hours('shared_hours', 'Shared'),
    col.count('competitors', 'Pairs'),
  ];
}
